import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { getQuoteBasedContainerApi, externalQuoteBottleOrderCartApi } from 'services';
import { useCart } from '../../../CartContext';

interface QuoteContainerCheckoutDialogProps {
    open: boolean;
    quote: any;
    quoteAnalysisReference?: any;
    clientId: any;
    onClose: () => void;
}

interface ContainerRow {
    id: any;
    containerID: any;
    name: string;
    description: string;
    noOfUnits: number | '';
}

interface TestContainerRow {
    id: any;
    testId: any;
    testName: string;
    quoteNumber: string;
    resultTableName: string;
    resultTableID: any;
    quoteID: any;
    units: number | '';
    expanded: boolean;
    containers: ContainerRow[];
    source: any;
    comment: string;
}


export const QuoteContainerCheckoutDialog = ({ open, quote, quoteAnalysisReference, clientId, onClose }: QuoteContainerCheckoutDialogProps) => {
    const [rows, setRows] = useState<TestContainerRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const { refreshCartItems, setQuoteAnalysisRefID } = useCart();
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [resultTableCollapsed, setResultTableCollapsed] = useState<
        Record<string, boolean>
    >({});

    const [resultTableComments, setResultTableComments] = useState<
        Record<string, string>
    >({});

    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

    const handleResultTableCollapse = (
        resultTableName: string,
        checked: boolean
    ) => {
        setResultTableCollapsed((prev) => ({
            ...prev,
            [resultTableName]: checked,
        }));

        // When checkbox is enabled, collapse ALL rows
        if (checked) {
            setRows((prevRows) =>
                prevRows.map((row) =>
                    row.resultTableName === resultTableName
                        ? {
                            ...row,
                            expanded: false,
                        }
                        : row
                )
            );
        }
    };

    const handleResultTableCommentChange = (
        resultTableName: string,
        value: string
    ) => {
        setResultTableComments((prev) => ({
            ...prev,
            [resultTableName]: value,
        }));
    };

    const quoteNumber = rows[0]?.quoteNumber || quote?.quoteNumber || '-';

    const resultTableSections = rows.reduce((sections: any[], row: any) => {
        const resultTableName = row.resultTableName || 'Result Table';

        let section = sections.find(
            (item) => item.resultTableID === row.resultTableID
        );

        if (!section) {
            section = {
                resultTableID: row.resultTableID,
                quoteID: row.quoteID,
                resultTableName,
                title: resultTableName,
                rows: [],
            };

            sections.push(section);
        }

        section.rows.push(row);

        return sections;
    }, []);

    const mapQuoteContainers = (items: any[]): TestContainerRow[] => {
        return items.map((item: any, index: number) => {
            const units = item?.size ?? item?.noOfUnits ?? 0;
            const testId = item?.testID ?? item?.testId ?? item?.id ?? index;

            return {
                id: `${item?.resultTableID ?? 'table'}-${testId}`,
                testId,
                testName: item?.testName || item?.groupName || `Test #${index + 1}`,
                quoteNumber: item?.quoteNumber || quote?.quoteNumber || '',

                // ADD THESE
                resultTableName: item?.resultTableName || '',
                resultTableID: item?.resultTableID ?? null,
                quoteID: item?.quoteID ?? null,

                units,
                expanded: false,

                containers: Array.isArray(item?.draftItems)
                    ? item.draftItems.map(
                        (draftItem: any, childIndex: number) => ({
                            id:
                                draftItem?.id ||
                                draftItem?.containerID ||
                                `${index}-${childIndex}`,
                            containerID: draftItem?.containerID,
                            name: draftItem?.containerName || '',
                            description: draftItem?.containerDesc || '',
                            noOfUnits: draftItem?.noOfUnits ?? units,
                        })
                    )
                    : [],

                source: item,
                comment: '',
            };
        });
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        setResultTableCollapsed({});
        setResultTableComments({});
    }, [open]);

    useEffect(() => {
        if (!open) {
            setRows([]);
            return;
        }

        // Internal flow already has the quote's analysis tests cached on the bottle group, so skip the API call
        if (Array.isArray(quoteAnalysisReference?.analysisTests) && quoteAnalysisReference.analysisTests.length > 0) {
            setRows(mapQuoteContainers(quoteAnalysisReference.analysisTests));
            return;
        }

        if (!quote?.id) {
            setRows([]);
            return;
        }

        getQuoteContainer();
    }, [open, quote?.id, clientId, quoteAnalysisReference]);

    const getQuoteContainer = () => {
        setLoading(true);
        getQuoteBasedContainerApi(quote?.id, clientId).then((res: any) => {
            if (res?.status === 200 && Array.isArray(res?.data) && res.data.length > 0) {
                setRows(mapQuoteContainers(res.data));
            } else {
                setRows([]);
                setSnackbar({ open: true, message: res?.data, severity: 'error' });
            }
        }).catch(() => {
            setRows([]);
            setSnackbar({ open: true, message: 'Failed to fetch quote containers.', severity: 'error' });
        }).finally(() => {
            setLoading(false);
        });
    }

    const handleUnitsChange = (rowId: any, value: string) => {
        const nextUnits = value === '' ? '' : Math.max(0, Number(value));
        setRows((prevRows) => prevRows.map((row) => row.id === rowId ? {
            ...row,
            units: nextUnits,
            containers: row.containers.map((container) => ({ ...container, noOfUnits: nextUnits }))
        } : row));
    };

    const handleToggleExpand = (rowId: any) => {
        setRows((prevRows) => prevRows.map((row) => row.id === rowId ? { ...row, expanded: !row.expanded } : row));
    };

    const handleDeleteParent = (rowId: any) => {
        setRows((prevRows) => prevRows.filter((row) => row.id !== rowId));
    };

    const handleDeleteResultTable = (resultTableName: string) => {
        setRows((prevRows) => prevRows.filter((row) => row.resultTableName !== resultTableName));
    };

    const handleAddToCheckout = async () => {
        const selectedSections = resultTableSections.filter((section) => {
            const tableKey = section.resultTableName || 'result-table';

            return !!resultTableCollapsed[tableKey];
        });

        // Build the payload
        const payload = {
            clientID: clientId,
            quoteID: quote?.id,
            analysisReferenceDTOs: selectedSections.map((section) => {
                const tableKey = section.resultTableName || 'result-table';

                return {
                    quoteId: section.quoteID,
                    resultTableID: section.resultTableID,
                    comments: resultTableComments[tableKey] || '',
                };
            }),
        };

        if (!clientId) {
            setSnackbar({
                open: true,
                message: 'Please select a client before adding to checkout.',
                severity: 'warning',
            });
            return;
        }

        if (!selectedSections.length) {
            setSnackbar({
                open: true,
                message: 'Please select at least one result table.',
                severity: 'warning',
            });
            return;
        }

        // Make sure every selected section has a comment
        const hasEmptyComment = selectedSections.some((section) => {
            const tableKey = section.resultTableName || 'result-table';
            const comment = resultTableComments[tableKey] || '';

            return !comment.trim();
        });

        if (hasEmptyComment) {
            setSnackbar({
                open: true,
                message: 'Please provide a comment for all selected result tables.',
                severity: 'warning',
            });
            return;
        }

        setAdding(true);

        try {

            const response = await externalQuoteBottleOrderCartApi(payload);

            if (response.status === 200) {
                setResultTableComments({});
                setQuoteAnalysisRefID(response?.data?.quoteAnalysisRefID || null);
                await refreshCartItems(clientId);
                onClose();

                setSnackbar({
                    open: true,
                    message: 'Items successfully added to checkout.',
                    severity: 'success'
                });
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to add items to checkout.',
                    severity: 'error'
                });
            }
        } finally {
            setAdding(false);
        }
    };

    return (
        <>
            {rows.length > 0 && (
                <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h6" component="div">
                            Quote Containers
                        </Typography>

                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : rows.length === 0 ? (
                            <Typography sx={{ py: 3 }} color="text.secondary">No containers found for this quote.</Typography>
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 700, ml: 'auto', mr: 1, mb: 2, textAlign: 'right' }}>
                                    Quote Number: {quoteNumber}
                                </Typography>
                                {resultTableSections.map((section) => {
                                    const tableKey =
                                        section.resultTableName || 'result-table';

                                    const isNoResult =
                                        !!resultTableCollapsed[tableKey];

                                    const comment =
                                        resultTableComments[tableKey] || '';

                                    return (
                                        <Box
                                            key={tableKey}
                                            sx={{
                                                mb: 2,
                                            }}
                                        >
                                            {/* =========================
                Section Header
            ========================== */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    mb: 1,
                                                }}
                                            >
                                                {/* Left - Title + Checkbox */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {section.title}
                                                    </Typography>

                                                    {labUserCheck?.UserType === 'External' && (
                                                        <Checkbox
                                                            size="small"
                                                            checked={isNoResult}
                                                            onChange={(event) =>
                                                                handleResultTableCollapse(
                                                                    tableKey,
                                                                    event.target.checked
                                                                )
                                                            }
                                                            sx={{
                                                                p: 0.5,
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                {/* Right - Delete */}
                                                {labUserCheck?.UserType === 'External' && <Tooltip title="Delete">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            handleDeleteResultTable(
                                                                section.resultTableName
                                                            )
                                                        }
                                                        size="small"
                                                    >
                                                        <DeleteOutlineOutlinedIcon />
                                                    </IconButton>
                                                </Tooltip>}
                                            </Box>

                                            {/* =========================
                Result Table
            ========================== */}
                                            <TableContainer>
                                                <Table
                                                    sx={{
                                                        minWidth: 700,
                                                        border: '1px solid #ddd',
                                                    }}
                                                >
                                                    {/* Table Header */}
                                                    <TableHead
                                                        sx={{
                                                            backgroundColor: '#003883',
                                                        }}
                                                    >
                                                        <TableRow>
                                                            <TableCell
                                                                sx={{
                                                                    color: '#fff',
                                                                    fontWeight: 600,
                                                                    padding: '14px',
                                                                }}
                                                            >
                                                                Test Name
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>

                                                    {/* =========================
                        Table Body
                    ========================== */}
                                                    <TableBody>
                                                        {section.rows.map((row) => (
                                                            <React.Fragment key={row.id}>
                                                                {/* Parent/Test Row */}
                                                                <TableRow
                                                                    sx={{
                                                                        backgroundColor:
                                                                            '#f5f7fa',
                                                                    }}
                                                                >
                                                                    {/* Test Name */}
                                                                    <TableCell
                                                                        sx={{
                                                                            padding: '8px',
                                                                        }}
                                                                    >
                                                                        {labUserCheck?.UserType !== 'External' ? <Link
                                                                            component="button"
                                                                            variant="body2"
                                                                            onClick={() =>
                                                                                handleToggleExpand(
                                                                                    row.id
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            {row.testName}{' '}
                                                                            {row.expanded
                                                                                ? '(-)'
                                                                                : '(+)'}
                                                                        </Link> : <Typography variant="body2">{row.testName}</Typography>}
                                                                    </TableCell>
                                                                </TableRow>

                                                                {/* =========================
                                    Child Container Rows
                                ========================== */}
                                                                {row.expanded &&
                                                                    row.containers.map(
                                                                        (container) => (
                                                                            <TableRow
                                                                                key={`${row.id}-${container.containerID}`}
                                                                            >
                                                                                <TableCell
                                                                                    sx={{
                                                                                        pl: 4,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        container.name
                                                                                    }
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        )
                                                                    )}
                                                            </React.Fragment>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>

                                            {/* =========================
                Mandatory Reason
                Only when No Result checked
            ========================== */}
                                            {labUserCheck?.UserType === 'External' && isNoResult ? (
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={3}
                                                    size="small"
                                                    label="Comment"
                                                    placeholder="Please provide a comment"
                                                    value={comment}
                                                    required
                                                    inputProps={{
                                                        maxLength: 300,
                                                    }}
                                                    onChange={(event) =>
                                                        handleResultTableCommentChange(
                                                            tableKey,
                                                            event.target.value
                                                        )
                                                    }
                                                    error={!comment.trim()}
                                                    helperText={
                                                        !comment.trim()
                                                            ? 'Comment is required'
                                                            : `${comment.length}/300 characters`
                                                    }
                                                    sx={{
                                                        mt: 2,
                                                    }}
                                                />
                                            ) : labUserCheck?.UserType !== 'External' &&
                                            (<TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={3}
                                                    size="small"
                                                    label="Comment"
                                                    value={quoteAnalysisReference?.comments || ''}
                                                    disabled={labUserCheck?.UserType !== 'External'}
                                                    inputProps={{
                                                        maxLength: 300,
                                                    }}
                                                    sx={{
                                                        mt: 2,
                                                        '& .MuiInputBase-input.Mui-disabled': {
                                                            WebkitTextFillColor: '#000',
                                                            color: '#000',
                                                            opacity: 1,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}


                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                        >
                            {labUserCheck?.UserType !== 'Internal' ? 'Cancel' : 'OK'}
                        </Button>

                        {labUserCheck?.UserType !== 'Internal' &&
                            <Button
                                variant="contained"
                                onClick={handleAddToCheckout}
                                disabled={
                                    labUserCheck?.UserType === 'External'
                                        ? resultTableSections.length === 0 ||
                                        !resultTableSections.some((section) => {
                                            const tableKey =
                                                section.resultTableName || 'result-table';

                                            return !!resultTableCollapsed[tableKey];
                                        }) ||
                                        resultTableSections.some((section) => {
                                            const tableKey =
                                                section.resultTableName || 'result-table';

                                            const isChecked =
                                                !!resultTableCollapsed[tableKey];

                                            const comment =
                                                resultTableComments[tableKey] || '';

                                            return isChecked && !comment.trim();
                                        })
                                        : false
                                }
                            >
                                Add to Checkout
                            </Button>}
                    </DialogActions>

                </Dialog>
            )}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};
